import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { _js } from '@ifixit/localize';
import { Validation } from 'Shared/utils';
import { checkEmailAccountExists } from '../services/authService';
import { withLoadingIndicator } from '../utils/loadingUtils';

interface UseEmailValidationProps {
   onNavigateToLogin: (email: string) => void;
   onNavigateToRegister: (email: string) => void;
}

/**
 * Validates email format according to app requirements
 */
const validateEmailFormat = (email: string): string => {
   if (!email || !Validation.email(email)) {
      return _js('Please enter a valid email address.');
   }
   return '';
};

export const useEmailValidation = ({
   onNavigateToLogin,
   onNavigateToRegister,
}: UseEmailValidationProps) => {
   const [error, setError] = useState<string>('');

   const emailCheckMutation = useMutation({
      mutationFn: checkEmailAccountExists,
      onSuccess: (response, email) => {
         if (response.hasAccount) {
            onNavigateToLogin(email);
         } else {
            onNavigateToRegister(email);
         }
      },
      onError: error => {
         console.error('Error in email validation:', error);
         setError(_js('An unexpected error occurred. Please try again.'));
      },
   });

   // eslint-disable-next-line react-hooks/use-memo
   const validateEmail = useCallback(validateEmailFormat, []);

   const clearError = useCallback(() => {
      setError('');
   }, []);

   const submitEmail = useCallback(
      async (email: string) => {
         const validationError = validateEmailFormat(email);
         if (validationError) {
            setError(validationError);
            return;
         }

         setError('');

         try {
            await withLoadingIndicator(emailCheckMutation.mutateAsync(email), {
               pendingMessage: _js('Checking email...'),
               successMessage: '',
               failureMessage: _js('Email check failed.'),
            });
         } catch {
            // Error is already handled in onError callback
         }
      },
      [emailCheckMutation]
   );

   return {
      isSubmitting: emailCheckMutation.isPending,
      error,
      validateEmail,
      clearError,
      submitEmail,
   };
};
